/// Loading the Google Maps JavaScript API, and the geometry the route pages
/// need on top of it.
///
/// Loaded on demand rather than from index.html: the map is one page of four,
/// and the API is a large script plus a billable map load. Somebody looking at
/// Today should not pay for either.

import { config } from './config';

const METRES_PER_MILE = 1609.344;

/// The colour a route is drawn in, in one place rather than seven. Blue
/// because that is what a route line means on every map anyone has used, and
/// because the accent has to be one colour, not one per file.
export const ROUTE_STROKE = '#007aff';

export const metresToMiles = (metres: number) => metres / METRES_PER_MILE;

/// Memoised, because every caller wants the same single global and the script
/// can only be injected once. The rejected case is deliberately not cached —
/// a key that arrives late, or a network blip, should be retryable.
let pending: Promise<typeof google.maps> | null = null;

export function loadMaps(): Promise<typeof google.maps> {
  if (pending) return pending;

  pending = new Promise<typeof google.maps>((resolve, reject) => {
    if (!config.mapsKey) {
      reject(new Error('No Google Maps key configured. Set VITE_GOOGLE_MAPS_KEY.'));
      return;
    }
    if (window.google?.maps) {
      resolve(window.google.maps);
      return;
    }

    // `loading=async` is what Google asks for and silences the console warning;
    // the callback is still how you know it is ready.
    const callback = '__stepsMapsReady';
    (window as unknown as Record<string, unknown>)[callback] = () => {
      resolve(window.google.maps);
    };

    const script = document.createElement('script');
    script.src =
      'https://maps.googleapis.com/maps/api/js'
      + `?key=${encodeURIComponent(config.mapsKey)}`
      + '&libraries=places,geometry'
      + `&callback=${callback}`
      + '&loading=async';
    script.async = true;
    script.onerror = () => reject(new Error('Could not load Google Maps.'));
    document.head.appendChild(script);
  }).catch((failure: unknown) => {
    pending = null;
    throw failure;
  });

  return pending;
}

/// A point some way along a path, with the direction of travel there.
export interface PositionOnPath {
  point: google.maps.LatLng;
  /// Degrees clockwise from north, for aiming a Street View camera down the
  /// route rather than at whatever it happens to face.
  heading: number;
}

/**
 * Walk `miles` along `path` and report where you are.
 *
 * Segment lengths are accumulated rather than assuming an even spacing: a
 * decoded polyline is dense through towns and sparse across a straight
 * interstate, so treating vertices as equidistant would put you hundreds of
 * miles out.
 *
 * Clamps at both ends. Before the start is the start; past the finish is the
 * finish, which is the honest answer for a walk that has overshot its route.
 */
export function positionAtMiles(
  maps: typeof google.maps,
  path: google.maps.LatLng[],
  miles: number,
): PositionOnPath | null {
  if (path.length === 0) return null;
  const first = path[0];
  if (!first) return null;
  if (path.length === 1) return { point: first, heading: 0 };

  const target = Math.max(0, miles) * METRES_PER_MILE;
  let travelled = 0;

  for (let i = 0; i < path.length - 1; i += 1) {
    const from = path[i];
    const to = path[i + 1];
    if (!from || !to) continue;

    const segment = maps.geometry.spherical.computeDistanceBetween(from, to);
    if (travelled + segment >= target) {
      // Guard the zero-length segment: duplicated vertices do occur, and the
      // fraction would be a division by zero.
      const fraction = segment === 0 ? 0 : (target - travelled) / segment;
      return {
        point: maps.geometry.spherical.interpolate(from, to, fraction),
        heading: maps.geometry.spherical.computeHeading(from, to),
      };
    }
    travelled += segment;
  }

  const last = path[path.length - 1];
  const previous = path[path.length - 2];
  if (!last || !previous) return null;
  return {
    point: last,
    heading: maps.geometry.spherical.computeHeading(previous, last),
  };
}

/// A decoded path with its length measured once.
///
/// Snapping a dragged marker back onto the line means comparing it against
/// every segment, on every drag event, and a transcontinental overview
/// polyline runs to thousands of vertices. Measuring the segments up front
/// turns each of those comparisons into arithmetic rather than a few thousand
/// trigonometric calls.
export interface MeasuredPath {
  points: google.maps.LatLng[];
  /// Cumulative metres to each vertex: `metres[i]` is the distance from the
  /// start of the line to `points[i]`, so the last entry is the whole length.
  metres: number[];
  /// That last entry, in miles. Used to check a saved route still measures
  /// what it claimed when it was saved.
  miles: number;
}

export function measurePath(
  maps: typeof google.maps,
  points: google.maps.LatLng[],
): MeasuredPath {
  const metres: number[] = [];
  let total = 0;
  for (let i = 0; i < points.length; i += 1) {
    const from = points[i - 1];
    const to = points[i];
    if (i > 0 && from && to) total += maps.geometry.spherical.computeDistanceBetween(from, to);
    metres.push(total);
  }
  return { points, metres, miles: metresToMiles(total) };
}

const RADIANS = Math.PI / 180;

/// `point` relative to `origin`, in degrees, with longitude scaled so that it
/// is worth what it is at this latitude rather than what it is at the equator.
///
/// A flat frame is enough here: it is only ever used across a single polyline
/// segment — metres to a few kilometres — where the curvature error is orders
/// of magnitude below the precision of a mouse click.
function offset(origin: google.maps.LatLng, point: google.maps.LatLng) {
  return {
    x: (point.lng() - origin.lng()) * Math.cos(origin.lat() * RADIANS),
    y: point.lat() - origin.lat(),
  };
}

/// A point on a route, and how far along it lies.
export interface PointOnPath {
  point: google.maps.LatLng;
  miles: number;
}

/**
 * The point on `path` closest to `target`, and how far along the line it is.
 *
 * The inverse of `positionAtMiles`, and the whole of turning a click on the
 * map into a mileage. Google's geometry library has no closest-point helper —
 * `poly.isLocationOnEdge` only answers yes or no — so each segment is
 * projected onto in turn and the nearest wins.
 *
 * Ties go to the earlier segment, which matters for a route that doubles back
 * on itself: a click on the shared stretch reads as the first pass along it.
 */
export function nearestPointOnPath(
  maps: typeof google.maps,
  path: MeasuredPath,
  target: google.maps.LatLng,
): PointOnPath | null {
  const { points, metres } = path;
  const first = points[0];
  if (!first) return null;
  if (points.length === 1) return { point: first, miles: 0 };

  let best: PointOnPath | null = null;
  let bestDistance = Infinity;

  for (let i = 0; i < points.length - 1; i += 1) {
    const from = points[i];
    const to = points[i + 1];
    const before = metres[i];
    const after = metres[i + 1];
    if (!from || !to || before === undefined || after === undefined) continue;

    const segment = offset(from, to);
    const relative = offset(from, target);
    const lengthSquared = segment.x * segment.x + segment.y * segment.y;
    // Duplicated vertices do occur, and the fraction would divide by zero.
    const fraction = lengthSquared === 0
      ? 0
      : Math.min(1, Math.max(0,
        (relative.x * segment.x + relative.y * segment.y) / lengthSquared));

    // Perpendicular distance in the same flat frame. Only its ordering is
    // used, so it never needs converting to metres.
    const dx = relative.x - segment.x * fraction;
    const dy = relative.y - segment.y * fraction;
    const distance = dx * dx + dy * dy;
    if (distance >= bestDistance) continue;

    bestDistance = distance;
    best = {
      point: maps.geometry.spherical.interpolate(from, to, fraction),
      miles: metresToMiles(before + (after - before) * fraction),
    };
  }

  return best;
}

/**
 * A short name for somewhere, for prefilling a checkpoint's label.
 *
 * "Hays, KS" rather than the geocoder's full postal address, which for a
 * point on an interstate is a house number nobody wanted. Empty when the
 * geocoder has nothing — a checkpoint's label is optional, and a failed
 * lookup is not worth an error in front of someone.
 *
 * Billed per request, so this is called once on placing a checkpoint rather
 * than on every drag event.
 */
export async function describePoint(
  maps: typeof google.maps,
  point: google.maps.LatLng,
): Promise<string> {
  try {
    const { results } = await new maps.Geocoder().geocode({ location: point });
    const [nearest] = results;
    if (!nearest) return '';

    const named = (type: string) =>
      nearest.address_components.find((part) => part.types.includes(type));
    // postal_town is what the UK has instead of a locality.
    const town = named('locality') ?? named('postal_town') ?? named('administrative_area_level_2');
    const region = named('administrative_area_level_1');
    if (town && region) return `${town.long_name}, ${region.short_name}`;
    return town?.long_name ?? region?.long_name ?? nearest.formatted_address;
  } catch {
    return '';
  }
}
